import React, { useState, useEffect } from 'react';
import { RefreshCw, Hourglass, PlusCircle, Settings, AlertCircle } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { quadrantInfo } from './quadrantInfo';

const EmptyStateGuide = ({ onOpenCategoryContext }) => (
  <div className="bg-white rounded-lg shadow-lg p-4 mt-4 mx-4">
    <div className="flex items-center pb-2 border-b border-gray-200">
      <Hourglass size={24} className="mr-2 text-purple-600" />
      <h2 className="text-xl font-bold">Next Hour Suggestion</h2>
    </div>

    <div className="mt-4 space-y-6">
      <div className="bg-purple-50 p-4 rounded-lg">
        <h3 className="font-semibold text-purple-800 mb-2">How to Get Started</h3>
        <p className="text-purple-900">
          The AI-powered Next Hour Suggestion feature helps you prioritize tasks and maximize your productivity.
          Here's how to get the most out of it:
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            <PlusCircle size={20} className="text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium mb-1">1. Add Your Tasks</h4>
            <p className="text-gray-600">Start by adding your tasks to the Eisenhower Matrix. Each task will be categorized based on its urgency and importance.</p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            <Settings size={20} className="text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium mb-1">2. Set Up Category Context</h4>
            <p className="text-gray-600">
              Personalize your suggestions by
              <button
                onClick={onOpenCategoryContext}
                className="text-purple-600 hover:text-purple-700 font-medium mx-1"
              >
                adding category context
              </button>
              for different types of tasks (Work, Personal, etc.).
            </p>
          </div>
        </div>

        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 mt-1">
            <AlertCircle size={20} className="text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium mb-1">3. Get Personalized Suggestions</h4>
            <p className="text-gray-600">Once you've added tasks and context, you'll receive AI-powered suggestions tailored to your needs and preferences.</p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg mt-4">
        <p className="text-sm text-blue-800">
          <strong>Pro Tip:</strong> The more context you provide for each category, the more personalized and relevant your suggestions will be!
        </p>
      </div>
    </div>
  </div>
);

const AIHourlySuggestion = ({ notes, userId, userPreferences, onOpenCategoryContext }) => {
  const [suggestion, setSuggestion] = useState('');
  const [status, setStatus] = useState('initializing');
  const [statusMessage, setStatusMessage] = useState('Initializing...');
  const [activeQuadrant, setActiveQuadrant] = useState(null);
  const [error, setError] = useState(null);
  const quadrantUrgencyOrder = ['doFirst', 'delegate', 'decide', 'delete'];

  useEffect(() => {
    determineActiveQuadrant();
    initializeSuggestion();
  }, [notes]);

  const determineActiveQuadrant = () => {
    for (const quadrant of quadrantUrgencyOrder) {
      const quadrantNotes = notes.filter(note => note.quadrant === quadrant);
      if (quadrantNotes.length > 0) {
        setActiveQuadrant(quadrant);
        return;
      }
    }
    setActiveQuadrant(null);
  };

  const initializeSuggestion = async () => {
    if ((!userId) || suggestion) return;

    setStatus('loading');
    setStatusMessage('Loading suggestion...');
    setError(null);

    try {
      const db = getFirestore();
      const suggestionRef = doc(db, 'users', userId, 'suggestions', 'latest');
      const suggestionDoc = await getDoc(suggestionRef);

      if (!suggestionDoc.exists()) {
        // Initialize with empty content if document doesn't exist
        await setDoc(suggestionRef, { content: '' });
        setSuggestion('');
      } else {
        setSuggestion(suggestionDoc.data().content);
      }
      setStatus('ready');
    } catch (error) {
      console.error('Error loading suggestion:', error);
      setError('Failed to load suggestion');
      setStatus('error');
    }
  };

  const generateNewSuggestion = async () => {
    if (!userId || !activeQuadrant) return;

    setStatus('loading');
    setStatusMessage('Generating new suggestion...');
    setError(null);

    try {
      const quadrantNotes = notes
        .filter(note => note.quadrant === activeQuadrant)
        .map(({ category, content, dueAt, title }) => ({
          category,
          content: typeof content === 'string' ? content : JSON.stringify(content),
          dueAt: dueAt ? new Date(dueAt).toISOString() : null,
          title
        }));

      if (quadrantNotes.length === 0) {
        setStatus('ready');
        return;
      }

      const functions = getFunctions();
      const nextHourSuggestion = httpsCallable(functions, 'nextHourSuggestion');

      // Include category contexts from user preferences
      const categoryContexts = userPreferences?.categoryContexts || {};

      const dataToSend = {
        notes: quadrantNotes,
        quadrantInfo: {
          label: quadrantInfo[activeQuadrant].label,
          sublabel: quadrantInfo[activeQuadrant].sublabel
        },
        categoryContexts
      };

      const result = await nextHourSuggestion(dataToSend);
      const newSuggestion = result.data.suggestion;

      // Store the new suggestion in Firestore
      const db = getFirestore();
      await setDoc(doc(db, 'users', userId, 'suggestions', 'latest'), {
        content: newSuggestion
      });

      setSuggestion(newSuggestion);
      setStatus('ready');
    } catch (error) {
      console.error('Error generating suggestion:', error);
      setStatus('error');
      setError(error.message || 'Failed to generate suggestion. Please try again.');
    }
  };

  if (notes.length === 0) {
    return <EmptyStateGuide onOpenCategoryContext={onOpenCategoryContext} />;
  }

  if (!activeQuadrant || notes.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 mt-4 mx-4">
      <div className="flex items-center justify-between pb-2 border-b border-gray-200">
        <h2 className="text-xl font-bold flex items-center">
          <Hourglass size={24} className="mr-2" />
          Next Hour Suggestion
        </h2>
        <button
          onClick={generateNewSuggestion}
          disabled={status === 'loading'}
          className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-2 rounded-full hover:opacity-90 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-purple-400 disabled:opacity-50"
        >
          <RefreshCw size={16} className={status === 'loading' ? "animate-spin" : ""} />
        </button>
      </div>
      <div className="mt-4">
        {status === 'loading' && (
          <div className="space-y-2 mb-4">
            <div className="flex items-center">
              <div className="animate-spin mr-2">
                <RefreshCw size={16} />
              </div>
              <p className="text-gray-600">{statusMessage}</p>
            </div>
            <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 animate-pulse"></div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-red-600">
            {error}
          </div>
        )}

        {suggestion && (
          <div className="text-gray-800 whitespace-pre-wrap">
            {suggestion}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIHourlySuggestion;